/**
 * CallScriptModal — full-screen call-script panel.
 *
 * Triggered from the prospect drawer. Generates a discovery-call script on
 * open. The user can scroll the script during the call, or copy any phase.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2, Phone, X, Copy, Check, Target, AlertTriangle, Sparkles } from 'lucide-react';
import { useCallScript, type CallScript } from '@/hooks/useProspect';
import { ApiError } from '@/lib/api';
import { IneaMark } from '@/components/IneaMark';

interface CallScriptModalProps {
  prospectId: string;
  prospectName: string;
  open: boolean;
  onClose: () => void;
}

export function CallScriptModal({ prospectId, prospectName, open, onClose }: CallScriptModalProps) {
  const { t } = useTranslation();
  const mutation = useCallScript(prospectId);
  const [script, setScript] = useState<CallScript | null>(null);

  // Auto-generate the script the first time the modal opens.
  useEffect(() => {
    if (!open) return;
    if (script || mutation.isPending) return;
    mutation.mutate(undefined, { onSuccess: (data) => setScript(data.script) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-night/50 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8"
      >
        <div className="card-hi flex h-full max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-[26px]">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-ink/[0.07] px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue to-violet text-white">
                <Phone size={18} />
              </span>
              <div>
                <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-pink">
                  Script d'appel · Inea
                </p>
                <h2 className="font-display text-[17px] font-800 tracking-[-0.02em]">
                  Préparer l'appel avec {prospectName}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full bg-ink/[0.05] p-2 text-ink/55 transition hover:bg-ink/10 hover:text-ink"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {mutation.isPending && !script ? (
              <Generating />
            ) : mutation.isError && !script ? (
              <p className="rounded-xl bg-pink/10 px-4 py-3 text-[13px] font-600 text-pink-deep">
                {mutation.error instanceof ApiError ? mutation.error.message : t('callScript.error')}
              </p>
            ) : script ? (
              <ScriptView script={script} />
            ) : null}
          </div>

          {/* Footer */}
          {script && (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-ink/[0.07] bg-white/40 px-6 py-3.5">
              <p className="text-[11.5px] text-ink/55">
                <Sparkles size={11} className="mr-1 inline" />
                {t('callScript.goodLuck')}
              </p>
              <button
                onClick={() => {
                  setScript(null);
                  mutation.mutate(undefined, { onSuccess: (d) => setScript(d.script) });
                }}
                disabled={mutation.isPending}
                className="btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-60"
              >
                {mutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
                Régénérer
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

function Generating() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center py-16 text-center">
      <IneaMark size={56} />
      <Loader2 className="mt-4 animate-spin text-pink" size={22} />
      <p className="mt-4 font-display text-[15px] font-800 tracking-[-0.02em]">
        {t('callScript.preparing')}
      </p>
      <p className="mt-1 text-[12.5px] text-ink/55">
        {t('callScript.preparingSub')}
      </p>
    </div>
  );
}

function ScriptView({ script }: { script: CallScript }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      {script.objective && (
        <section className="rounded-2xl bg-gradient-to-br from-violet/[0.07] to-pink/[0.05] p-4">
          <p className="kicker text-violet">
            <Target size={11} /> Objectif
          </p>
          <p className="mt-1.5 text-[13.5px] leading-[1.55] text-ink/85">{script.objective}</p>
        </section>
      )}

      {script.opening && (
        <PhaseBlock
          label="Ouverture"
          duration="30 sec"
          purpose={t('callScript.openerPurpose')}
          content={script.opening}
        />
      )}

      {script.phases.map((phase, i) => (
        <PhaseBlock
          key={i}
          label={phase.name}
          duration={phase.duration}
          purpose={phase.purpose}
          bullets={phase.bullets}
        />
      ))}

      {script.close && (
        <PhaseBlock
          label="Conclusion"
          duration="—"
          purpose={t('callScript.nextPurpose')}
          content={script.close}
          highlight
        />
      )}

      {script.watch_outs.length > 0 && (
        <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
          <p className="kicker text-amber-700">
            <AlertTriangle size={11} /> À surveiller
          </p>
          <ul className="mt-1.5 space-y-1">
            {script.watch_outs.map((w, i) => (
              <li key={i} className="flex gap-2 text-[12.5px] leading-[1.5] text-ink/75">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                {w}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PhaseBlock({
  label,
  duration,
  purpose,
  bullets,
  content,
  highlight,
}: {
  label: string;
  duration: string;
  purpose?: string;
  bullets?: string[];
  content?: string;
  highlight?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  function copy(): void {
    const txt = content ?? (bullets ?? []).join('\n• ');
    void navigator.clipboard.writeText(content ? content : `• ${txt}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <section
      className={`rounded-2xl border p-4 ${
        highlight ? 'border-pink/40 bg-pink/[0.04]' : 'border-ink/[0.08] bg-white/70'
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="font-display text-[14px] font-800 tracking-[-0.015em]">{label}</h4>
          <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/55">
            {duration}
          </span>
        </div>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] font-700 text-ink/60 transition hover:bg-pink/10 hover:text-pink"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
      {purpose && <p className="mb-2 text-[11.5px] italic text-ink/55">{purpose}</p>}
      {content && <p className="text-[13.5px] leading-[1.55] text-ink/85">« {content} »</p>}
      {bullets && bullets.length > 0 && (
        <ul className="space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-[13px] leading-[1.5] text-ink/80">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-blue to-pink" />
              {b}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
